import React, { useCallback, useContext, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { isObject } from 'lodash'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import InternshipsRequestsTable from './InternshipsRequestsTable'
import {
  deleteInternshipRequest, getAllInternshipRequests, refuseInternshipRequest, updateInternshipRequest
} from '../../../../utils/api/internshipRequest'
import { onError, onSuccess } from '../../../../utils/apiHelper'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import InstitutionModal from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { SchoolContext } from '../../../../Providers/SchoolProvider'
import useQuery from '../../../../hooks/useQuery'
import { StateTypes } from '../../../../utils/entities/internshipRequest'

const { key: initChecked, values: stateValues } = StateTypes.OPEN

const InternshipsRequestsView = () => {
  const sc = useContext(SchoolContext)

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const [parameters, setParameters] = useState(new TableParameters({
    filters: { states: stateValues },
    order: { by: OrderBy.CREATED_AT, direction: OrderDirections.DESC }
  }))

  const internshipRequests = useQuery({
    request: getAllInternshipRequests,
    defaultParameters: useMemo(() => parameters.toQueryParameters(), [parameters])
  })

  const handleDelete = useCallback(request => {
    deleteInternshipRequest(user, request).then(json => {
      onSuccess(t('internship_request.delete.success'))
      internshipRequests.refetch()
    }).catch(() => {
      onError(t('internship_request.delete.error'))
    })
  }, [user, t, internshipRequests.refetch])

  const handleRefuse = useCallback(request => {
    refuseInternshipRequest(user, request).then(json => {
      if (json?.data) {
        onSuccess(t('internship_request.refuse.success'))
        internshipRequests.refetch()
      } else {
        onError(t('internship_request.refuse.error'))
      }
    })
  }, [user, t, internshipRequests.refetch])

  const handleLinkInstitution = useCallback((request, institution, sector) => {
    const body = { id: request.id, institution: institution.id }

    if (isObject(sector)) {
      body.sector = sector.id
    }

    updateInternshipRequest(user, body).then(() => {
      onSuccess(t('internship_request.update_institution.success'))
      internshipRequests.refetch()
    }).catch(() => {
      onError(t('internship_request.update_institution.error'))
    }).finally(() => {
      sc.setInstitutions(data => data.some(i => i.id === institution.id) ? data : [...data, institution])
    })
  }, [user, internshipRequests.refetch])

  const handlePaginationChange = useCallback(targetPage => {
    const { first, last, prev, next } = internshipRequests.metadata

    switch (targetPage) {
      case first: internshipRequests.first()
        break
      case last: internshipRequests.last()
        break
      case next: internshipRequests.next()
        break
      case prev: internshipRequests.previous()
        break
      default:
        break
    }
  }, [internshipRequests.metadata])

  const handleParametersChange = useCallback(tableParameters => {
    const queryParameters = new TableParameters({
      ...internshipRequests.parameters,
      ...tableParameters
    })
    setParameters(queryParameters)
    const { page } = internshipRequests.metadata
    if (queryParameters.page !== page) {
      handlePaginationChange(queryParameters.page)
    } else {
      internshipRequests.setParameters(queryParameters.toQueryParameters())
    }
  }, [internshipRequests.parameters, internshipRequests.metadata, handlePaginationChange])

  const handleFiltersChange = useCallback(states => {
    handleParametersChange(new TableParameters({
      ...internshipRequests.parameters, filters: { states }
    }))
  }, [internshipRequests.parameters])

  return (
    <div>
      <InstitutionModal>
        <InternshipsRequestsTable
          data={internshipRequests.data}
          institution={user?.institutions[0]}
          school={user?.school}
          initChecked={initChecked}
          loading={internshipRequests.loading}
          metadata={internshipRequests.metadata}
          parameters={parameters}
          onDelete={handleDelete}
          onParametersChange={handleParametersChange}
          onFilterChange={handleFiltersChange}
          onRefuse={handleRefuse}
          onLinkInstitution={handleLinkInstitution}
          reload={internshipRequests.refetch}
        />
      </InstitutionModal>
    </div>
  )
}

export default InternshipsRequestsView
